// Pages/propiedades/components/EliminarPropiedadModal.tsx
//
// BORRADO DE UNA PROPIEDAD, CON REVISIÓN OBLIGATORIA (round 2026-08-06).
//
// Antes esto era un Popconfirm: "¿Seguro?" → borraba todo → recién después
// mostraba un resumen de lo que se había llevado. Y ese resumen describía lo
// que las consultas HABÍAN hecho, no lo que debían hacer: el inventario se
// contaba por un criterio (ubicación) y las transacciones se borraban por otro
// (origen/destino), así que en cada borrado se perdía plata en las dos
// direcciones sin que nadie se enterara.
//
// Ahora el borrado es un flujo de dos partes:
//
//   1. BLOQUEANTES (inventario) — no se tocan sin decisión explícita:
//      • Stock vivo en la propiedad o su bodeguita: hay que sacarlo de ahí
//        (moverlo desde /inventario) o aceptar que se elimine con su plata.
//      • Historial de compras que menciona la propiedad pero cuyo producto vive
//        en otra parte: casi siempre hay que CONSERVARLO — la propiedad se va,
//        pero el gasto de AF ocurrió igual y esa transacción suele ser el único
//        registro de lo que costó la mercadería.
//      Cada fila tiene "Ver historia": la línea de tiempo completa del producto
//      para decidir con contexto en vez de a ciegas.
//
//   2. CASCADA — el resto (arriendos, aseos, lavados, documentos, flujos…),
//      contado con los MISMOS predicados con los que después se borra.
//
// El backend recalcula todo y responde 409 si algo quedó sin confirmar, así que
// esta pantalla no es la única defensa: si otro admin carga una compra mientras
// este modal está abierto, el borrado se detiene y vuelve a mostrar el estado.
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ExclamationCircleOutlined,
  HistoryOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { propiedadesApi } from "../../../services/propiedades.api";
import HistoriaProductoDrawer from "../../inventario/components/HistoriaProductoDrawer";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import "../../inventario/components/historiaProducto.css";

const { Text, Paragraph } = Typography;

const clp = (n: number | null | undefined) =>
  `$${Number(n || 0).toLocaleString("es-CL")}`;

interface ProductoBloqueante {
  id: number;
  nombre: string;
  categoria: string | null;
  oculto: boolean;
  cantidadUnidades: number;
  cantidadPacks: number;
  precioPorUnidad: number;
  precioPorPack: number;
  esPack: boolean;
  ubicacionEtiqueta: string;
  ubicacionNombre: string | null;
}

interface TransaccionBloqueante {
  id: number;
  tipo: string;
  fecha: string;
  productoNombre: string;
  inventarioId: number | null;
  productoExiste: boolean;
  montoTotal: number;
  origenNombre: string | null;
  destinoNombre: string | null;
  rolPropiedad: "origen" | "destino";
  flujosVinculados: number;
  montoFlujos: number;
  flujosPendientes: number;
}

interface Impacto {
  propiedad: { id: number; nombre: string };
  bloqueantes: {
    productos: ProductoBloqueante[];
    transacciones: TransaccionBloqueante[];
    resumen: {
      productos: number;
      transacciones: number;
      transaccionesHuerfanas: number;
      montoProductos: number;
      montoTransacciones: number;
      flujosVinculados: number;
      flujosPendientes: number;
    };
  };
  cascada: {
    clave: string;
    etiqueta: string;
    modo: string;
    nota: string | null;
    filas: number;
  }[];
  totalCascada: number;
  puedeEliminar: boolean;
}

interface Props {
  propiedadId: number | null;
  nombrePropiedad: string;
  open: boolean;
  onClose: () => void;
  /**
   * Ejecuta el borrado. Devuelve `true` si se completó.
   *
   * El modal NO llama a la API por su cuenta: el borrado sigue pasando por
   * `usePropiedadesLogic`, que es quien refresca el listado, ajusta la
   * paginación y arma el toast con el resumen. Duplicar la llamada acá sería
   * abrir un segundo camino que puede divergir del primero — justamente el
   * error que esta pantalla existe para reparar.
   */
  onConfirmar: (confirmacion: {
    eliminarProductos: number[];
    conservarTransacciones: number[];
  }) => Promise<boolean>;
}

const EliminarPropiedadModal: React.FC<Props> = ({
  propiedadId,
  nombrePropiedad,
  open,
  onClose,
  onConfirmar,
}) => {
  const [cargando, setCargando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [impacto, setImpacto] = useState<Impacto | null>(null);
  const [confirmacion, setConfirmacion] = useState("");
  const [aceptaProductos, setAceptaProductos] = useState(false);
  const [aceptaTransacciones, setAceptaTransacciones] = useState(false);
  const [historiaId, setHistoriaId] = useState<number | null>(null);
  const [historiaNombre, setHistoriaNombre] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!propiedadId) return;
    setCargando(true);
    const res = await propiedadesApi.getImpactoBorrado(propiedadId);
    setImpacto(res?.data ?? null);
    setCargando(false);
  }, [propiedadId]);

  useEffect(() => {
    if (open && propiedadId) {
      void cargar();
      setConfirmacion("");
      setAceptaProductos(false);
      setAceptaTransacciones(false);
    }
    if (!open) setImpacto(null);
  }, [open, propiedadId, cargar]);

  const bloq = impacto?.bloqueantes;
  const hayProductos = (bloq?.resumen.productos ?? 0) > 0;
  const hayTransacciones = (bloq?.resumen.transacciones ?? 0) > 0;

  // El nombre exacto es la última barrera: obliga a leer QUÉ se está borrando.
  // Se compara sin distinguir mayúsculas ni espacios de más porque la UI muestra
  // los nombres de propiedad en mayúsculas (formatPropertyName) y sería una
  // trampa pedir que se escriban tal cual se ven.
  const nombreOk =
    confirmacion.trim().toLowerCase() === nombrePropiedad.trim().toLowerCase();

  const puedeBorrar =
    !!impacto &&
    nombreOk &&
    (!hayProductos || aceptaProductos) &&
    (!hayTransacciones || aceptaTransacciones);

  const eliminar = async () => {
    if (!propiedadId || !impacto) return;
    setBorrando(true);
    const ok = await onConfirmar({
      eliminarProductos: bloq?.productos.map((p) => p.id) ?? [],
      conservarTransacciones: bloq?.transacciones.map((t) => t.id) ?? [],
    });
    setBorrando(false);
    if (ok) {
      onClose();
      return;
    }
    // 409 por carrera (otro admin cargó una compra entre el previo y la
    // confirmación) o error real: recargamos el previo para que el admin vea el
    // estado actual en vez de reintentar contra una foto vieja, y le pedimos
    // aceptar de nuevo — puede haber aparecido algo que todavía no miró.
    void cargar();
    setAceptaProductos(false);
    setAceptaTransacciones(false);
  };

  const abrirHistoria = (id: number | null, nombre: string) => {
    if (id == null) return;
    setHistoriaId(id);
    setHistoriaNombre(nombre);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width={900}
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: "#cf1322", marginRight: 8 }} />
            Eliminar {formatPropertyName(nombrePropiedad)}
          </span>
        }
        footer={[
          <Button key="cancelar" onClick={onClose}>
            Cancelar
          </Button>,
          <Button
            key="eliminar"
            danger
            type="primary"
            loading={borrando}
            disabled={!puedeBorrar}
            onClick={() => void eliminar()}
          >
            Eliminar definitivamente
          </Button>,
        ]}
        destroyOnClose
      >
        {cargando && (
          <div className="historia-cargando">
            <Spin tip="Calculando qué se va a eliminar…" />
          </div>
        )}

        {!cargando && !impacto && (
          <Alert
            type="error"
            showIcon
            message="No se pudo calcular el impacto del borrado"
            description="Sin ese cálculo no se puede eliminar la propiedad de forma segura. Vuelve a intentarlo."
          />
        )}

        {!cargando && impacto && (
          <>
            <Alert
              type="error"
              showIcon
              message="Esto es irreversible"
              description="El borrado es físico: no hay papelera ni undo. Si solo quieres que la propiedad deje de aparecer para clientes y aseadores, usa el interruptor «habilitado» en vez de eliminarla."
            />

            {/* ── Stock vivo ────────────────────────────────────────────── */}
            {hayProductos && (
              <div className="borrado-propiedad-seccion">
                <div className="borrado-propiedad-titulo">
                  <InboxOutlined />
                  Stock en esta propiedad ({bloq?.resumen.productos}) —{" "}
                  {clp(bloq?.resumen.montoProductos)}
                </div>
                <Paragraph type="secondary">
                  Estos productos están físicamente acá. La ubicación va a
                  desaparecer, así que o los mueves a otra bodega desde
                  /inventario, o se eliminan con toda su plata e historial.
                </Paragraph>
                <Table<ProductoBloqueante>
                  size="small"
                  rowKey="id"
                  dataSource={bloq?.productos ?? []}
                  pagination={
                    (bloq?.productos.length ?? 0) > 5 ? { pageSize: 5 } : false
                  }
                  columns={[
                    {
                      title: "Producto",
                      dataIndex: "nombre",
                      render: (v: string, row) => (
                        <span>
                          {v}
                          {row.oculto && (
                            <Tag color="default" style={{ marginLeft: 6 }}>
                              oculto
                            </Tag>
                          )}
                        </span>
                      ),
                    },
                    {
                      title: "Dónde",
                      key: "ubicacion",
                      render: (_: unknown, row) =>
                        row.ubicacionNombre
                          ? `${row.ubicacionEtiqueta}: ${row.ubicacionNombre}`
                          : row.ubicacionEtiqueta,
                    },
                    {
                      title: "Cantidad",
                      key: "cantidad",
                      align: "right",
                      render: (_: unknown, row) =>
                        row.esPack
                          ? `${row.cantidadPacks} packs (${row.cantidadUnidades} u.)`
                          : `${row.cantidadUnidades} u.`,
                    },
                    {
                      title: "",
                      key: "acciones",
                      width: 110,
                      render: (_: unknown, row) => (
                        <Button
                          size="small"
                          icon={<HistoryOutlined />}
                          onClick={() => abrirHistoria(row.id, row.nombre)}
                        >
                          Historia
                        </Button>
                      ),
                    },
                  ]}
                />
                <Checkbox
                  className="borrado-propiedad-confirm"
                  checked={aceptaProductos}
                  onChange={(e) => setAceptaProductos(e.target.checked)}
                >
                  Entiendo que estos {bloq?.resumen.productos} producto(s) y su
                  historial se eliminan junto con la propiedad.
                </Checkbox>
              </div>
            )}

            {/* ── Historial de compras que menciona la propiedad ────────── */}
            {hayTransacciones && (
              <div className="borrado-propiedad-seccion">
                <div className="borrado-propiedad-titulo">
                  <HistoryOutlined />
                  Historial de compras que menciona esta propiedad (
                  {bloq?.resumen.transacciones}) — {clp(bloq?.resumen.montoTransacciones)}
                </div>
                <Alert
                  type="info"
                  showIcon
                  className="historia-alerta"
                  message="Estas transacciones SE CONSERVAN"
                  description="Pertenecen a productos que viven en otra ubicación (o cuyo producto ya no existe). Son plata real que AF gastó y suelen ser el único registro de lo que costó esa mercadería: borrarlas haría desaparecer un egreso verdadero de las Compras del mes. Si alguna no debería existir, elimínala desde Inventario → Finanzas antes de continuar."
                />
                {(bloq?.resumen.transaccionesHuerfanas ?? 0) > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    className="historia-alerta"
                    message={`${bloq?.resumen.transaccionesHuerfanas} de ellas ya no tienen producto`}
                    description="Son transacciones huérfanas: quedaron colgando de un producto borrado. Igual siguen sumando como egreso. Puedes revisarlas con «Historia» y eliminarlas desde Inventario → Finanzas."
                  />
                )}
                <Table<TransaccionBloqueante>
                  size="small"
                  rowKey="id"
                  dataSource={bloq?.transacciones ?? []}
                  pagination={
                    (bloq?.transacciones.length ?? 0) > 5 ? { pageSize: 5 } : false
                  }
                  columns={[
                    { title: "Fecha", dataIndex: "fecha", width: 110 },
                    {
                      title: "Producto",
                      dataIndex: "productoNombre",
                      render: (v: string, row) => (
                        <span>
                          {v}
                          {!row.productoExiste && (
                            <Tag color="red" style={{ marginLeft: 6 }}>
                              sin producto
                            </Tag>
                          )}
                        </span>
                      ),
                    },
                    {
                      title: "Ruta",
                      key: "ruta",
                      render: (_: unknown, row) =>
                        `${row.origenNombre ?? "—"} → ${row.destinoNombre ?? "—"}`,
                    },
                    {
                      title: "Monto",
                      dataIndex: "montoTotal",
                      align: "right",
                      width: 110,
                      render: (v: number) => clp(v),
                    },
                    {
                      title: "Flujos",
                      key: "flujos",
                      width: 90,
                      align: "right",
                      render: (_: unknown, row) =>
                        row.flujosVinculados > 0 ? (
                          <Tooltip
                            title={`${clp(row.montoFlujos)} en cobros/reembolsos${
                              row.flujosPendientes > 0
                                ? ` · ${row.flujosPendientes} pendiente(s)`
                                : ""
                            }`}
                          >
                            <Tag color={row.flujosPendientes > 0 ? "orange" : "blue"}>
                              {row.flujosVinculados}
                            </Tag>
                          </Tooltip>
                        ) : (
                          <Text type="secondary">—</Text>
                        ),
                    },
                    {
                      title: "",
                      key: "acciones",
                      width: 110,
                      render: (_: unknown, row) => (
                        <Button
                          size="small"
                          icon={<HistoryOutlined />}
                          disabled={row.inventarioId == null}
                          onClick={() =>
                            abrirHistoria(row.inventarioId, row.productoNombre)
                          }
                        >
                          Historia
                        </Button>
                      ),
                    },
                  ]}
                />
                <Checkbox
                  className="borrado-propiedad-confirm"
                  checked={aceptaTransacciones}
                  onChange={(e) => setAceptaTransacciones(e.target.checked)}
                >
                  Revisé estas {bloq?.resumen.transacciones} transacciones y
                  confirmo conservarlas como historia.
                </Checkbox>
              </div>
            )}

            {/* ── El resto de la cascada ────────────────────────────────── */}
            <div className="borrado-propiedad-seccion">
              <div className="borrado-propiedad-titulo">
                Lo demás que se elimina ({impacto.totalCascada} registros)
              </div>
              {impacto.cascada.length === 0 ? (
                <Empty description="Nada más asociado" />
              ) : (
                <div className="borrado-propiedad-cascada">
                  {impacto.cascada.map((paso) => (
                    <Tooltip key={paso.clave} title={paso.nota ?? undefined}>
                      <div
                        className={`borrado-propiedad-cascada-item${
                          paso.filas === 0 ? " borrado-propiedad-cascada-cero" : ""
                        }`}
                      >
                        <span>{paso.etiqueta}</span>
                        <strong>
                          {paso.filas}
                          {paso.modo === "update" ? " (se actualizan)" : ""}
                        </strong>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>

            {/* ── Confirmación final ────────────────────────────────────── */}
            <div className="borrado-propiedad-seccion">
              <Text>
                Para confirmar, escribe el nombre de la propiedad:{" "}
                <strong>{nombrePropiedad}</strong>
              </Text>
              <Input
                className="borrado-propiedad-confirm"
                value={confirmacion}
                onChange={(e) => setConfirmacion(e.target.value)}
                placeholder={nombrePropiedad}
                status={confirmacion && !nombreOk ? "error" : undefined}
              />
            </div>
          </>
        )}
      </Modal>

      <HistoriaProductoDrawer
        inventarioId={historiaId}
        nombreFallback={historiaNombre}
        open={historiaId != null}
        onClose={() => setHistoriaId(null)}
      />
    </>
  );
};

export default EliminarPropiedadModal;
